import {
  CREATE_HUB_CART,
  DELETE_HUB_CART,
  FETCH_HUB_CART_BY_ID,
  FETCH_HUB_CARTS,
  FETCH_HUB_CARTS_BY_USER,
  SEARCH_HUB_CARTS,
  UPDATE_HUB_CART,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubCart } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { HUB_PACKAGES_KEY } from "./HubPackages";

export const HUB_CARTS_KEY = "hubCartsKey";
export const HUB_CART_BY_ID_KEY = "hubCartByIdKey";
export const HUB_CARTS_BY_USER_KEY = "hubCartsByUserKey";

export const getHubCartsKey = () => [HUB_CARTS_KEY] as const;

export const getHubCartByIdKey = (cartId: number) =>
  [HUB_CART_BY_ID_KEY, cartId] as const;

export const getHubCartsByUserKey = (userId: string) =>
  [HUB_CARTS_BY_USER_KEY, userId] as const;

export const useHubCarts = (
  options?: Partial<
    UseQueryOptions<
      IHubCart[],
      AxiosError,
      IHubCart[],
      ReturnType<typeof getHubCartsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCartsKey(),
    queryFn: async () => {
      return mutationFetch<IHubCart[]>({
        url: FETCH_HUB_CARTS,
        method: "GET",
      });
    },
    ...options,
  });
};

export const useHubCart = (
  cartId: number,
  options?: Partial<
    UseQueryOptions<
      IHubCart,
      AxiosError,
      IHubCart,
      ReturnType<typeof getHubCartByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCartByIdKey(cartId),
    queryFn: async () => {
      return mutationFetch<IHubCart>({
        url: FETCH_HUB_CART_BY_ID.replace(":cartId", String(cartId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && cartId > 0,
  });
};

export const useHubCurrentCart = (
  userId: string,
  options?: Partial<
    UseQueryOptions<
      IHubCart,
      AxiosError,
      IHubCart,
      ReturnType<typeof getHubCartsByUserKey>
    >
  >,
) => {
  const trimmed = userId.trim();
  return useQuery({
    queryKey: getHubCartsByUserKey(trimmed),
    queryFn: async () => {
      return mutationFetch<IHubCart>({
        url: FETCH_HUB_CARTS_BY_USER.replace(
          ":userId",
          encodeURIComponent(trimmed),
        ),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && trimmed.length > 0,
  });
};

const createHubCart = (body: IHubCart): Promise<IHubCart> => {
  return mutationFetch<IHubCart>({
    url: CREATE_HUB_CART,
    method: "POST",
    body,
  });
};

const updateHubCart = (cartId: number, body: IHubCart): Promise<IHubCart> => {
  return mutationFetch<IHubCart>({
    url: UPDATE_HUB_CART.replace(":cartId", String(cartId)),
    method: "PUT",
    body,
  });
};

const deleteHubCart = (cartId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_HUB_CART.replace(":cartId", String(cartId)),
    method: "DELETE",
  });
};

export const useCreateHubCart = (
  options?: UseMutationOptions<IHubCart, AxiosError, IHubCart>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HUB_CARTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CARTS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CARTS_BY_USER_KEY] });
    },
    ...options,
  });
};

export const useUpdateHubCart = (
  options?: UseMutationOptions<
    IHubCart,
    AxiosError,
    IHubCart & { _cartId?: number }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IHubCart & { _cartId?: number }) => {
      const cartId = body._cartId ?? body.cartId;
      if (cartId == null) {
        throw new Error("cartId is required for update");
      }
      const { _cartId, ...rest } = body;
      return updateHubCart(cartId, rest as IHubCart);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_CARTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CARTS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CARTS_BY_USER_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_PACKAGES_KEY] });
      if (data.cartId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCartByIdKey(data.cartId),
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubCart = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubCart,
    onSuccess: (_, cartId) => {
      queryClient.invalidateQueries({ queryKey: [HUB_CARTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CARTS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CARTS_BY_USER_KEY] });
      queryClient.invalidateQueries({
        queryKey: getHubCartByIdKey(cartId),
      });
    },
    ...options,
  });
};

export const useSearchHubCarts = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubCart>, "url" | "payload">,
) => {
  return useSearchQuery<IHubCart>({
    url: SEARCH_HUB_CARTS,
    payload,
    ...options,
  });
};
